import React from "react";

/**
 * MysteryCard — the one physical object the whole match flow hands around.
 *
 * The same card back appears as a map beacon, as the docked card in the Match
 * Deck, as the clone that flies between them, and as the face-down card the
 * reveal flips. Rendering it from one component is not a tidiness choice: the
 * illusion that it is a single object being moved depends on the material,
 * edge lighting and highlight angle matching exactly. Two hand-tuned copies
 * drift, and the flight starts to look like a crossfade between two cards.
 *
 * Anatomy, back to front:
 *   two offset back layers   physical thickness
 *   the face                 violet foil gradient, lit from upper-left
 *   inner hairline           the printed border
 *   top highlight            the light source
 *   the `?`                  never card art, never a name (see §9 privacy)
 *   angled sweep             one foil reflection, optional
 *
 * Deliberately has no map, deck or session knowledge — it takes a size and a
 * couple of flags, so it can be dropped into any of those four places.
 */
const MysteryCard = ({
  width,
  height,
  radius = 8,
  glyphSize,
  /** Dimmer treatment for a non-selected beacon. */
  muted = false,
  /** One-shot foil sweep, e.g. as a beacon lands. */
  glint = false,
  /** Slow ambient sheen for the resting deck card. */
  sheen = false,
  /** Small violet check — this match has been opened at least once. */
  explored = false,
  style,
  ...rest
}) => {
  const glyph = glyphSize ?? Math.round(width * 0.4);
  // Thickness offsets scale with the card, so a 26px anchor doesn't show the
  // same dark slabs sticking out behind it that a 46px beacon can carry.
  const back1 = `translate(${(width * 0.048).toFixed(1)}px, ${(-width * 0.043).toFixed(1)}px) scale(0.98)`;
  const back2 = `translate(${(width * 0.098).toFixed(1)}px, ${(-width * 0.087).toFixed(1)}px) scale(0.955)`;
  const backOpacity = muted ? 0.6 : 1;

  return (
    <span
      data-mystery-card=""
      style={{
        position: "relative",
        display: "block",
        width,
        height,
        ...style,
      }}
      {...rest}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius,
          background: "#2b1360",
          transform: back2,
          opacity: backOpacity * 0.55,
        }}
      />
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius,
          background: "#3a1a78",
          transform: back1,
          opacity: backOpacity * 0.8,
        }}
      />
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius,
          overflow: "hidden",
          background: muted
            ? "linear-gradient(150deg,#8b5cf6,#5b21b6)"
            : "linear-gradient(150deg,#a78bfa 0%,#7c3aed 42%,#4c1d95 100%)",
          border: `1px solid ${muted ? "rgba(196,181,253,0.4)" : "rgba(237,233,254,0.6)"}`,
          boxShadow: muted
            ? "0 8px 15px -6px rgba(46,16,101,0.48)"
            : "0 16px 26px -8px rgba(46,16,101,0.75), 0 0 22px -6px rgba(139,92,246,0.5)",
          display: "grid",
          placeItems: "center",
          color: "#f5f3ff",
          fontSize: glyph,
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        <span
          style={{
            position: "absolute",
            inset: Math.max(2, Math.round(width * 0.055)),
            borderRadius: Math.max(3, radius - 2),
            border: "1px solid rgba(237,233,254,0.22)",
          }}
        />
        <span
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            height: "52%",
            background: "linear-gradient(180deg, rgba(255,255,255,0.2), transparent)",
          }}
        />
        <span style={{ position: "relative", top: -0.5 }}>?</span>

        {(glint || sheen) && (
          <span
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(118deg, transparent 32%, rgba(255,255,255,0.55) 50%, transparent 68%)",
              opacity: glint ? 0 : 1,
              animation: glint
                ? "mysteryCardGlint 620ms ease-out 80ms 1"
                : "mysteryCardSheen 3200ms ease-in-out infinite",
            }}
          />
        )}
      </span>

      {explored && (
        <span
          data-testid="mystery-card-explored"
          style={{
            position: "absolute",
            top: -Math.round(width * 0.14),
            right: -Math.round(width * 0.14),
            width: Math.max(14, Math.round(width * 0.34)),
            height: Math.max(14, Math.round(width * 0.34)),
            borderRadius: "50%",
            background: "#7c3aed",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 2px 6px rgba(46,16,101,0.5)",
          }}
        >
          <svg
            width={Math.max(8, Math.round(width * 0.2))}
            height={Math.max(8, Math.round(width * 0.2))}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
      )}

      <style>{`
        @keyframes mysteryCardGlint {
          from { transform: translateX(-150%) skewX(-16deg); opacity: 1; }
          to   { transform: translateX(270%)  skewX(-16deg); opacity: 1; }
        }
        @keyframes mysteryCardSheen {
          0%       { transform: translateX(-130%) skewX(-14deg); }
          55%,100% { transform: translateX(230%)  skewX(-14deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-mystery-card] * { animation: none !important; }
        }
      `}</style>
    </span>
  );
};

export default MysteryCard;
